import { zodResolver } from '@hookform/resolvers/zod'
import { KeyRound } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import * as authApi from '@/features/auth/auth-api'
import { useAuth } from '@/features/auth/auth-context'
import { Button } from '@/shared/components/ui/button'
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Field } from '@/shared/components/ui/field'
import { Input } from '@/shared/components/ui/input'

import { AuthShell } from '../components/auth-shell'

const loginSchema = z.object({
  login: z.string().min(1, 'Введите телефон или email'),
})

const smsSchema = z
  .object({
    code: z.string().regex(/^\d{4}$/, 'Введите код из 4 цифр'),
    password: z.string().min(8, 'Минимум 8 символов'),
    password_confirmation: z.string().min(8, 'Минимум 8 символов'),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Пароли не совпадают',
    path: ['password_confirmation'],
  })

type LoginForm = z.infer<typeof loginSchema>
type SmsForm = z.infer<typeof smsSchema>

type Step = 'login' | 'sms' | 'email-sent' | 'generic-sent'

function extractErrorMessage(error: unknown, fallback: string): string {
  const response = (error as { response?: { data?: { message?: string; retry_after?: number } } })
    ?.response?.data
  return response?.message || fallback
}

function extractRetryAfter(error: unknown): number | null {
  const retryAfter = (error as { response?: { data?: { retry_after?: number } } })?.response?.data
    ?.retry_after
  return typeof retryAfter === 'number' ? retryAfter : null
}

export function ForgotPasswordPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [step, setStep] = useState<Step>('login')
  const [login, setLogin] = useState('')
  const [resendIn, setResendIn] = useState(0)
  const [isResending, setIsResending] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [resetToken, setResetToken] = useState<string | null>(null)

  const loginForm = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { login: '' },
  })

  const smsForm = useForm<SmsForm>({
    resolver: zodResolver(smsSchema),
    defaultValues: { code: '', password: '', password_confirmation: '' },
  })

  useEffect(() => {
    if (resendIn <= 0) return
    const timer = setTimeout(() => setResendIn((value) => value - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendIn])

  if (isAuthenticated) {
    return <Navigate replace to="/projects" />
  }

  const requestReset = async (loginValue: string) => {
    const payload = await authApi.forgotPassword(loginValue, 'web')
    setLogin(loginValue)

    if (payload.channel === 'sms') {
      setStep('sms')
      setResendIn(payload.resend_after ?? 60)
      return
    }

    if (payload.channel === 'email') {
      setStep('email-sent')
      return
    }

    setStep('generic-sent')
  }

  const onSubmitLogin = loginForm.handleSubmit(async (values) => {
    setErrorMessage(null)

    try {
      await requestReset(values.login)
    } catch (error) {
      const retryAfter = extractRetryAfter(error)
      if (retryAfter !== null) {
        setResendIn(retryAfter)
      }
      setErrorMessage(extractErrorMessage(error, 'Не удалось отправить SMS. Попробуйте позже.'))
    }
  })

  const onResend = async () => {
    setErrorMessage(null)
    setIsResending(true)

    try {
      await requestReset(login)
    } catch (error) {
      const retryAfter = extractRetryAfter(error)
      if (retryAfter !== null) {
        setResendIn(retryAfter)
      }
      setErrorMessage(extractErrorMessage(error, 'Не удалось отправить SMS. Попробуйте позже.'))
    } finally {
      setIsResending(false)
    }
  }

  const onSubmitSms = smsForm.handleSubmit(async (values) => {
    setErrorMessage(null)

    try {
      const token = resetToken ?? (await authApi.verifyPasswordResetCode(login, values.code))
      setResetToken(token)
      await authApi.resetPasswordViaSms({
        login,
        reset_token: token,
        password: values.password,
        password_confirmation: values.password_confirmation,
      })
      navigate('/login', { replace: true })
    } catch (error) {
      setErrorMessage(
        extractErrorMessage(error, 'Не удалось сменить пароль. Проверьте код и попробуйте снова.'),
      )
    }
  })

  const description =
    step === 'login'
      ? 'Укажите телефон или email — мы отправим инструкции для восстановления.'
      : step === 'sms'
        ? `Код отправлен. Введите SMS-код и новый пароль для ${login}.`
        : step === 'email-sent'
          ? 'Если аккаунт найден, инструкции отправлены на email.'
          : 'Если аккаунт найден, инструкции отправлены.'

  return (
    <AuthShell>
      <Card className="w-full">
        <CardHeader>
          <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--color-accent)]/15 text-[var(--color-accent)]">
            <KeyRound className="h-5 w-5" />
          </div>
          <CardTitle>Восстановление пароля</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>

        <CardBody>
          {step === 'login' ? (
            <form className="space-y-4" onSubmit={onSubmitLogin}>
              <Field error={loginForm.formState.errors.login?.message} label="Телефон или email">
                {(props) => (
                  <Input
                    placeholder="+7 (999) 123-45-67 или you@company.com"
                    type="text"
                    {...loginForm.register('login')}
                    {...props}
                  />
                )}
              </Field>

              {errorMessage ? (
                <p className="text-sm text-[var(--color-danger-fg)]">{errorMessage}</p>
              ) : null}

              <Button className="w-full" loading={loginForm.formState.isSubmitting} type="submit">
                Продолжить
              </Button>
            </form>
          ) : null}

          {step === 'sms' ? (
            <form className="space-y-4" onSubmit={onSubmitSms}>
              <Field error={smsForm.formState.errors.code?.message} label="Код из SMS">
                {(props) => (
                  <Input
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="1234"
                    type="text"
                    {...smsForm.register('code')}
                    {...props}
                  />
                )}
              </Field>

              <Field error={smsForm.formState.errors.password?.message} label="Новый пароль">
                {(props) => (
                  <Input
                    autoComplete="new-password"
                    placeholder="••••••••"
                    type="password"
                    {...smsForm.register('password')}
                    {...props}
                  />
                )}
              </Field>

              <Field
                error={smsForm.formState.errors.password_confirmation?.message}
                label="Повтор пароля"
              >
                {(props) => (
                  <Input
                    autoComplete="new-password"
                    placeholder="••••••••"
                    type="password"
                    {...smsForm.register('password_confirmation')}
                    {...props}
                  />
                )}
              </Field>

              {errorMessage ? (
                <p className="text-sm text-[var(--color-danger-fg)]">{errorMessage}</p>
              ) : null}

              <Button className="w-full" loading={smsForm.formState.isSubmitting} type="submit">
                Сменить пароль
              </Button>

              <p className="text-sm text-[var(--color-text-soft)]">
                {resendIn > 0 ? (
                  <>Отправить код ещё раз можно через {resendIn} сек.</>
                ) : (
                  <button
                    className="font-medium text-[var(--color-accent)] hover:underline disabled:opacity-50"
                    disabled={isResending}
                    onClick={onResend}
                    type="button"
                  >
                    Отправить код ещё раз
                  </button>
                )}
              </p>
            </form>
          ) : null}

          {step === 'email-sent' ? (
            <div className="space-y-4">
              <p className="text-sm text-[var(--color-text-soft)]">
                Проверьте почту и перейдите по ссылке из письма, чтобы задать новый пароль.
              </p>
              <Link className="font-medium text-[var(--color-accent)] hover:underline" to="/login">
                Вернуться ко входу
              </Link>
            </div>
          ) : null}

          {step === 'generic-sent' ? (
            <div className="space-y-4">
              <p className="text-sm text-[var(--color-text-soft)]">
                Если аккаунт найден, инструкции отправлены.
              </p>
              <Link className="font-medium text-[var(--color-accent)] hover:underline" to="/login">
                Вернуться ко входу
              </Link>
            </div>
          ) : null}

          {step === 'login' ? (
            <p className="mt-4 text-sm text-[var(--color-text-soft)]">
              Вспомнили пароль?{' '}
              <Link className="font-medium text-[var(--color-accent)] hover:underline" to="/login">
                Войти
              </Link>
            </p>
          ) : null}
        </CardBody>
      </Card>
    </AuthShell>
  )
}
